import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import Swal from 'sweetalert2';
import {
  FaHistory,
  FaCalendarAlt,
  FaCheck,
  FaTimes,
  FaSpinner,
  FaInbox,
  FaClipboardList,
  FaClock,
  FaBan
} from 'react-icons/fa';
import API_BASE_URL from '../../../config/api';
import Topbar from '../mini-comp/Topbar';
import Sidebar from '../mini-comp/Sidebar';

const PayrollLeaveHistory = () => {
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLeave, setSelectedLeave] = useState(null);

  useEffect(() => {
    fetchLeaveHistory();
  }, []);

  const fetchLeaveHistory = async () => {
    try {
      const token = Cookies.get('token');
      const response = await axios.get(`${API_BASE_URL}/api/v1/payroll/leaves/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setLeaveHistory(response.data);
    } catch (error) {
      console.error('Error fetching leave history:', error);
      // Don't show error toast on fetch fail to avoid noise, unless it's critical
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleCancelLeave = async (leave, e) => {
    if (e) e.stopPropagation();

    try {
      const result = await Swal.fire({
        title: 'Cancel Leave Request',
        text: 'Are you sure you want to cancel this leave request?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, cancel it',
        cancelButtonText: 'No, keep it'
      });

      if (result.isConfirmed) {
        const token = Cookies.get('token');
        await axios.patch(`${API_BASE_URL}/api/v1.payroll/leaves/${leave.id}/`, {
          status: 'cancelled'
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        // Update the leave status in the local state
        setLeaveHistory(prevHistory =>
          prevHistory.map(item =>
            item.id === leave.id
              ? { ...item, status: 'cancelled' }
              : item
          )
        );

        Swal.fire({
          icon: 'success',
          title: 'Cancelled!',
          text: 'Your leave request has been cancelled.',
          timer: 2000,
          showConfirmButton: false,
          toast: true,
          position: 'top-end'
        });
      }
    } catch (error) {
      console.error('Error cancelling leave:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to cancel leave request'
      });
    }
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return <span className="badge bg-success-transparent text-success fs-12 fw-bold px-3 py-2 rounded-pill"><FaCheck className="me-1" /> Approved</span>;
      case 'rejected':
        return <span className="badge bg-danger-transparent text-danger fs-12 fw-bold px-3 py-2 rounded-pill"><FaTimes className="me-1" /> Rejected</span>;
      case 'cancelled':
        return <span className="badge bg-secondary-transparent text-secondary fs-12 fw-bold px-3 py-2 rounded-pill"><FaBan className="me-1" /> Cancelled</span>;
      default:
        return <span className="badge bg-warning-transparent text-warning fs-12 fw-bold px-3 py-2 rounded-pill"><FaClock className="me-1" /> Pending</span>;
    }
  };

  return (
    <div>
      <Topbar />
      <Sidebar />

      <div className="app-content-area">
        <div className="app-content-wrap">
          <div className="container-fluid">
            <div className="row">

              <div className="col-xl-12">
                <div className="card">
                  <h1 className="page-title fs-18 lh-1 d-flex align-items-center gap-10 mb-5">
                    <FaHistory className="text-primary" /> Leave History
                  </h1>
                  <hr />
                  <br />
                  <div className="card-body p-0">
                    {loading ? (
                      <div className="text-center py-5">
                        <FaSpinner className="fa-spin fs-40 text-primary mb-3" />
                        <p className="fs-16 text-muted">Loading leave history...</p>
                      </div>
                    ) : leaveHistory.length === 0 ? (
                      <div className="text-center py-5">
                        <div className="avatar avatar-xxl bg-light text-muted mb-4 mx-auto">
                          <FaInbox size={40} />
                        </div>
                        <h4 className="fw-bold">No Leave History Found</h4>
                        <p className="text-muted">You haven't made any leave requests yet.</p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                          <thead className="bg-light">
                            <tr>
                              <th className="ps-4">Leave Type</th>
                              <th>Duration</th>
                              <th>Period</th>
                              <th>Reason</th>
                              <th>Status</th>
                              <th className="text-end pe-4">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {leaveHistory.map((leave) => (
                              <tr key={leave.id}>
                                <td className="ps-4">
                                  <div className="d-flex align-items-center gap-10">
                                    <div className="avatar avatar-md bg-primary-transparent text-primary rounded-circle">
                                      <FaClipboardList />
                                    </div>
                                    <div>
                                      <h6 className="mb-0 fw-bold text-capitalize">{leave.leave_type}</h6>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <span className="fw-medium text-dark">
                                    {leave.duration} {leave.leave_type?.toLowerCase() === 'maternity' || leave.leave_type?.toLowerCase() === 'sick' ? 'Days' : 'Working Days'}
                                  </span>
                                </td>
                                <td>
                                  <div className="fs-13 text-muted">
                                    <FaCalendarAlt className="me-1 text-primary-light" />
                                    {formatDate(leave.start_date)} - {formatDate(leave.end_date)}
                                  </div>
                                </td>
                                <td>
                                  <div className="text-truncate" style={{ maxWidth: '200px' }} title={leave.reason}>
                                    {leave.reason || <span className="text-muted italic small">No reason provided</span>}
                                  </div>
                                </td>
                                <td>{getStatusBadge(leave.status)}</td>
                                <td className="text-end pe-4">
                                  {leave.status?.toLowerCase() === 'pending' && (
                                    <button
                                      className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-5"
                                      onClick={(e) => handleCancelLeave(leave, e)}
                                    >
                                      <FaBan size={12} /> Cancel
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayrollLeaveHistory;
